import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import { useTenancyLookup } from '../../utils/tenancyUtils';
import { getTextColor } from '../../utils/colorUtils';
import { ReactComponent as Tenants } from '../../assets/navigation-icons/tenantsSmall.svg';


const SharingDisplay = ({
    published = false,
    sharedTenancyIds = [],
    publishToFamily = false,
    variant = 'compact', // 'compact' | 'detailed'
    showTooltips = true,
    showTenancyDetails = true // Whether to show individual tenancy chips in detailed view
}) => {
    const [showTooltip, setShowTooltip] = useState(null);
    const theme = useTheme();


    // Get tenancy lookup data from Redux
    const { tenancyLookup, resolveTenancyName } = useTenancyLookup();

    // Get resolved tenancy names for display
    const getResolvedTenancyNames = () => {
        return sharedTenancyIds
            .filter(id => id !== 0) // Exclude global sharing ID
            .map(id => resolveTenancyName(id));
    };

    // Helper function to get sharing scope information
    const getSharingScope = () => {
        if (!published) {
            return {
                type: 'hidden',
                label: 'Hidden',
                color: theme.palette.grey[500],
                icon: <VisibilityOffIcon style={{ fontSize: '14px' }} />,
                description: 'Content is hidden from all users'
            };
        }

        if (sharedTenancyIds.includes(0)) {
            return {
                type: 'global',
                label: 'Global Access',
                color: theme.palette.primary.main,
                icon: <PublicIcon style={{ fontSize: '14px' }} />,
                description: 'Shared with all tenancies globally'
            };
        }

        if (sharedTenancyIds.length > 3) {
            return {
                type: 'multiple',
                label: `${sharedTenancyIds.length} Tenancies`,
                color: theme.palette.secondary.main,
                icon: <GroupIcon style={{ fontSize: '14px' }} />,
                description: `Shared with ${sharedTenancyIds.length} specific tenancies`
            };
        }

        if (sharedTenancyIds.length > 1) {
            return {
                type: 'multiple',
                label: `${sharedTenancyIds.length} Tenancies`,
                color: theme.palette.secondary.main,
                icon: <Tenants style={{ fontSize: '10px' }} />,
                description: `Shared with ${sharedTenancyIds.length} specific tenancies`
            };
        }

        return {
            type: 'single',
            label: '1 Tenancy',
            color: theme.palette.secondary.main,
            icon: <Tenants style={{ fontSize: '10px' }} />,
            description: 'Shared with 1 specific tenancy'
        };
    };

    const getDetailedTooltip = () => {
        const scope = getSharingScope();

        if (!published) {
            return 'Content is currently hidden from all users';
        }

        let tooltip = scope.description;

        if (sharedTenancyIds.includes(0)) {
            tooltip = 'Available to all tenancies';
        } else if (sharedTenancyIds.length > 0) {
            const resolvedNames = getResolvedTenancyNames();
            tooltip = `Shared with: ${resolvedNames.slice(0, 3).join(', ')}${resolvedNames.length > 3 ? ` and ${resolvedNames.length - 3} more` : ''}`;
        }

        if (published && publishToFamily && !sharedTenancyIds.includes(0)) {
            tooltip += '\n+ All child tenancies in hierarchy';
        }

        return tooltip;
    };

    const scope = getSharingScope();

    // Chip component styles - consistent sizing for both variants
    const chipStyles = {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '16px',
        fontSize: '0.75rem',
        fontWeight: '500',
        border: '1px solid',
        cursor: 'default',
        position: 'relative',
        gap: '4px'
    };

    const getChipStyle = (color, variant = 'filled') => ({
        ...chipStyles,
        backgroundColor: variant === 'filled' ? color : 'transparent',
        color: getTextColor(color),
        borderColor: color,
    });

    // Tooltip styles
    const tooltipStyles = {
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: '5px',
        backgroundColor: '#374151',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '0.75rem',
        whiteSpace: 'pre-line',
        zIndex: 1000,
        maxWidth: '250px',
        textAlign: 'center',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    };

    // Compact variant for table rows
    if (variant === 'compact') {
        return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div
                    style={getChipStyle(scope.color)}
                //onMouseEnter={() => showTooltips && setShowTooltip('main')}
                //onMouseLeave={() => setShowTooltip(null)}
                >
                    {scope.icon}
                    {scope.label}
                    {showTooltips && showTooltip === 'main' && (
                        <div style={tooltipStyles}>
                            {getDetailedTooltip()}
                        </div>
                    )}
                </div>

                {published && publishToFamily && !sharedTenancyIds.includes(0) && (
                    <div
                        style={getChipStyle(theme.palette.secondary.light)}
                    //onMouseEnter={() => setShowTooltip('family')}
                    //onMouseLeave={() => setShowTooltip(null)}
                    >
                        <FamilyRestroomIcon style={{ fontSize: '12px' }} />
                        Family
                        {showTooltip === 'family' && (
                            <div style={tooltipStyles}>
                                Includes all child tenancies in hierarchy
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // Detailed variant for previews and detail pages
    return (
        <div style={{
            padding: '16px',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.borderRadius.sm,
            backgroundColor: theme.palette.background.default
        }}>
            <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '1.1rem',
                fontWeight: '600',
                color: theme.palette.text.primary
            }}>
                Sharing & Visibility
            </h3>

            {/* Main status */}
            <div style={{ marginBottom: '16px' }}>
                <div style={{
                    ...getChipStyle(scope.color),
                    marginBottom: '8px'
                }}>
                    {scope.icon}
                    {scope.label}
                </div>
                <p style={{
                    margin: '0',
                    fontSize: '0.875rem',
                    color: theme.palette.text.secondary
                }}>
                    {scope.description}
                </p>
            </div>

            {/* Tenancy details */}
            {published && !sharedTenancyIds.includes(0) && sharedTenancyIds.length > 0 && showTenancyDetails && (
                <div style={{ marginBottom: '16px' }}>
                    <p style={{
                        margin: '0 0 8px 0',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        color: theme.palette.text.primary
                    }}>
                        Specific Tenancies:
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {sharedTenancyIds.slice(0, 6).map((tenancyId, index) => (
                            <div
                                key={tenancyId}
                                style={getChipStyle(theme.palette.grey[500])}
                            >
                                {resolveTenancyName(tenancyId)}
                            </div>
                        ))}
                        {sharedTenancyIds.length > 6 && (
                            <div style={getChipStyle(theme.palette.secondary.main)}
                            >
                                +{sharedTenancyIds.length - 6} more
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Family sharing */}
            {published && !sharedTenancyIds.includes(0) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{
                        marginTop: '2px',
                        color: publishToFamily ? theme.palette.secondary.main : theme.palette.grey[400]
                    }}>
                        <FamilyRestroomIcon style={{ fontSize: '20px' }} />
                    </div>
                    <div>
                        <p style={{
                            margin: '0',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: theme.palette.text.primary
                        }}>
                            Family Sharing: {publishToFamily ? "Enabled" : "Disabled"}
                        </p>
                        <p style={{
                            margin: '4px 0 0 0',
                            fontSize: '0.75rem',
                            color: theme.palette.text.secondary
                        }}>
                            {publishToFamily
                                ? "Also includes all child tenancies in hierarchy"
                                : "Limited to specifically selected tenancies only"
                            }
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SharingDisplay;